import * as React from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from '@/shared/utils/helpers'

function BottomSheet({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="bottom-sheet" {...props} />
}

function BottomSheetTrigger({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="bottom-sheet-trigger" {...props} />
}

function BottomSheetPortal({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="bottom-sheet-portal" {...props} />
}

function BottomSheetClose({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="bottom-sheet-close" {...props} />
}

function BottomSheetOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="bottom-sheet-overlay"
			className={cn(
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/30',
				className
			)}
			{...props}
		/>
	)
}

function BottomSheetContent({
	className,
	children,
	variant = 'drawer',
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
	/**
	 * Controls the sheet background styling.
	 * - drawer: blurred/translucent background (default, legacy behavior)
	 * - card: solid card background using --card
	 */
	variant?: 'drawer' | 'card'
}) {
	return (
		<BottomSheetPortal data-slot="bottom-sheet-portal">
			<BottomSheetOverlay />
			<DialogPrimitive.Content
				data-slot="bottom-sheet-content"
				className={cn(
					'h-fit-content fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl border-t shadow-lg duration-300',
					variant === 'drawer' ? 'drawer-blur' : 'bg-card',
					'data-[state=open]:animate-in data-[state=closed]:animate-out',
					'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
					'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
					'max-h-[90vh] overflow-y-auto',
					className
				)}
				{...props}
			>
				{/* Handle bar */}
				<div className="flex justify-center pt-3 pb-2">
					<div className="w-10 h-1 bg-gray-600 rounded-full" />
				</div>
				{children}
			</DialogPrimitive.Content>
		</BottomSheetPortal>
	)
}

function BottomSheetTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="bottom-sheet-title"
			className={cn('text-2xl leading-tight font-bold', className)}
			{...props}
		/>
	)
}

function BottomSheetDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="bottom-sheet-description"
			className={cn('text-muted-foreground text-base', className)}
			{...props}
		/>
	)
}

export {
	BottomSheet,
	BottomSheetClose,
	BottomSheetContent,
	BottomSheetDescription,
	BottomSheetOverlay,
	BottomSheetPortal,
	BottomSheetTitle,
	BottomSheetTrigger,
}
